import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion } from 'motion/react';

interface GardenFirefliesProps {
  count?: number;
  timeOfDay?: string;
  weather?: string;
}

interface FireflyData {
  id: number;
  baseX: number; // in percentage (vw)
  baseY: number; // in percentage (vh)
  size: number;
  pathX: number[];
  pathY: number[];
  duration: number;
  delay: number;
  blinkDuration: number;
  blinkDelay: number;
  glowColor: string;
  glowShadow: string;
}

// Generate organic wandering flight paths
function generateFlightPath(index: number): { pathX: number[]; pathY: number[]; duration: number } {
  const pattern = index % 5;
  const rand = (min: number, max: number) => min + Math.random() * (max - min);

  switch (pattern) {
    case 0: // Wide gentle wandering S-curve
      return {
        pathX: [0, rand(25, 55), rand(-10, 20), rand(-45, -15), rand(15, 40), 0],
        pathY: [0, rand(-35, -15), rand(-55, -30), rand(-25, 0), rand(10, 25), 0],
        duration: rand(11, 17),
      };
    case 1: // Hovering & dancing flutter around meadow
      return {
        pathX: [0, rand(-30, -10), rand(10, 35), rand(-20, 10), rand(25, 50), 0],
        pathY: [0, rand(15, 30), rand(-20, -5), rand(-45, -20), rand(-15, 10), 0],
        duration: rand(9, 14),
      };
    case 2: // Vertical floating ascension and glide
      return {
        pathX: [0, rand(-20, 20), rand(-35, -10), rand(20, 45), rand(-15, 15), 0],
        pathY: [0, rand(-40, -20), rand(-70, -45), rand(-50, -25), rand(-20, 0), 0],
        duration: rand(12, 18),
      };
    case 3: // Looping circular breeze
      return {
        pathX: [0, rand(35, 60), rand(45, 75), rand(15, 40), rand(-25, 5), 0],
        pathY: [0, rand(-25, -10), rand(10, 30), rand(25, 45), rand(-10, 15), 0],
        duration: rand(10, 15),
      };
    case 4: // Horizontal meadow glide
    default:
      return {
        pathX: [0, rand(-45, -20), rand(-70, -35), rand(-30, -5), rand(20, 45), 0],
        pathY: [0, rand(-15, 10), rand(-30, -10), rand(5, 25), rand(-10, 10), 0],
        duration: rand(13, 19),
      };
  }
}

const GLOW_PALETTES = [
  {
    core: '#fffde7',
    shadow: '0 0 10px 3px rgba(254, 240, 138, 0.9), 0 0 22px 6px rgba(234, 179, 8, 0.45)',
  },
  {
    core: '#fef08a',
    shadow: '0 0 12px 3px rgba(253, 224, 71, 0.95), 0 0 24px 7px rgba(245, 158, 11, 0.5)',
  },
  {
    core: '#ecfccb',
    shadow: '0 0 10px 3px rgba(190, 242, 100, 0.9), 0 0 20px 6px rgba(132, 204, 22, 0.4)',
  },
  {
    core: '#fffbeb',
    shadow: '0 0 14px 4px rgba(252, 211, 77, 0.95), 0 0 26px 8px rgba(245, 158, 11, 0.45)',
  },
];

export const GardenFireflies: React.FC<GardenFirefliesProps> = React.memo(({
  count = 22,
  timeOfDay = 'night',
  weather = 'sunny',
}) => {
  // Check if night/sunset & not heavy raining
  const isNightOrTwilight =
    (timeOfDay === 'night' || timeOfDay === 'sunset' || timeOfDay === 'dusk' || timeOfDay === 'dawn') &&
    weather !== 'rainy';

  const effectiveCount = Math.min(Math.max(count || 18, 10), 18);

  // Generate fireflies with stable initial values
  const firefliesList = useMemo<FireflyData[]>(() => {
    const list: FireflyData[] = [];
    for (let i = 0; i < effectiveCount; i++) {
      const flight = generateFlightPath(i);
      const palette = GLOW_PALETTES[i % GLOW_PALETTES.length];
      list.push({
        id: i + 1,
        baseX: 5 + (i * (90 / effectiveCount)) + (Math.random() * 8 - 4), // Distributed across width (5% - 95%)
        baseY: 28 + (Math.random() * 52), // Distributed across meadow & mid-sky (28% - 80%)
        size: 3.8 + Math.random() * 3.2, // 3.8px to 7px
        pathX: flight.pathX,
        pathY: flight.pathY,
        duration: flight.duration,
        delay: (i * 0.45) % 4,
        blinkDuration: 1.8 + Math.random() * 2.2, // 1.8s to 4s pulse
        blinkDelay: Math.random() * 2.5,
        glowColor: palette.core,
        glowShadow: palette.shadow,
      });
    }
    return list;
  }, [effectiveCount]);

  // Spark burst on interactive click or touch
  const handleFireflyClick = useCallback((e: React.MouseEvent, _f: FireflyData) => {
    e.stopPropagation();
    // Silent
  }, []);

  if (!isNightOrTwilight) return null;

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden z-25"
      aria-hidden="true"
      id="para-ti-firefly-swarm"
    >
      {firefliesList.map((f) => (
        <motion.div
          key={f.id}
          className="absolute pointer-events-auto cursor-pointer flex items-center justify-center p-2 -m-2 select-none"
          style={{
            left: `${f.baseX}%`,
            top: `${f.baseY}%`,
            willChange: 'transform',
          }}
          // Continuous 2D organic flight animation across the screen
          animate={{
            x: f.pathX,
            y: f.pathY,
          }}
          transition={{
            duration: f.duration,
            repeat: Infinity,
            repeatType: 'reverse',
            ease: 'easeInOut',
            delay: f.delay,
          }}
          whileHover={{ scale: 1.6 }}
          whileTap={{ scale: 2.2 }}
          onClick={(e) => handleFireflyClick(e, f)}
          title="Luciérnaga mágica — Toca para hacerla brillar ✨"
        >
          {/* Bioluminescent Pulsating Light Core */}
          <motion.div
            className="rounded-full relative"
            style={{
              width: `${f.size}px`,
              height: `${f.size}px`,
              backgroundColor: f.glowColor,
              boxShadow: f.glowShadow,
              willChange: 'transform, opacity',
            }}
            animate={{
              opacity: [0.2, 0.95, 0.35, 1, 0.25],
              scale: [0.85, 1.22, 0.92, 1.28, 0.88],
            }}
            transition={{
              duration: f.blinkDuration,
              repeat: Infinity,
              repeatType: 'mirror',
              ease: 'easeInOut',
              delay: f.blinkDelay,
            }}
          >
            {/* Subtle soft ambient aura halo */}
            <span
              className="absolute -inset-1 rounded-full opacity-35 pointer-events-none"
              style={{
                backgroundColor: f.glowColor,
                filter: 'blur(2px)',
              }}
            />
          </motion.div>
        </motion.div>
      ))}
    </div>
  );
});

GardenFireflies.displayName = 'GardenFireflies';
